import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { Plus, Minus, Move, RotateCw, X, Lock, Unlock, Maximize2, Minimize2 } from 'lucide-react';
import { API_URL } from '../config';

const ParkingMap = ({
    slots,
    logs,
    onSlotClick,
    selectedZone = 'ชั้น G',
    isAdmin = false,
    token,
    onRefresh,
    zones = [],
    onZoneChange,
    auditFilter = 'All',
    setAuditFilter,
    onMapClickToAdd
}) => {
    const [zoom, setZoom] = useState(1);
    const zoomRef = useRef(zoom);
    useEffect(() => {
        zoomRef.current = zoom;
    }, [zoom]);
    const [isEditMode, setIsEditMode] = useState(false);
    const [isAddModeMap, setIsAddModeMap] = useState(false);
    const [selectedSlotIds, setSelectedSlotIds] = useState(new Set()); // Multiple selection
    const [isDragging, setIsDragging] = useState(false);
    const [marquee, setMarquee] = useState(null); // Marquee selection box {start: {x,y}, end: {x,y}}
    const [dragStartPos, setDragStartPos] = useState(null);
    const [localSlots, setLocalSlots] = useState([]);
    const [isDimmed, setIsDimmed] = useState(true); // Background dimming state
    const [guides, setGuides] = useState({ x: null, y: null }); // Alignment guides
    const [isLocked, setIsLocked] = useState(false); // Default to unlocked for better desktop UX
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [isMapInitialLoading, setIsMapInitialLoading] = useState(true);
    const [isTransitioning, setIsTransitioning] = useState(false);
    const [isZoneListOpen, setIsZoneListOpen] = useState(false);
    const containerRef = useRef(null);
    const mapAreaRef = useRef(null);
    const mapRootRef = useRef(null); // Ref for the top-most container to request fullscreen on
    const touchStartDistRef = useRef(null);
    const touchStartZoomRef = useRef(null);

    // Sync local slots with props
    useEffect(() => {
        setLocalSlots(slots);
    }, [slots]);

    const fitToSlots = React.useCallback((targetSlots = slots) => {
        const slotsWithCoords = targetSlots.filter(s => s.coordinate_x !== null && s.coordinate_y !== null);
        if (slotsWithCoords.length === 0) {
            setZoom(1);
            setIsMapInitialLoading(false);
            setIsTransitioning(false);
            return;
        }

        // Get visible area dimensions
        if (!mapAreaRef.current) return;
        const rect = mapAreaRef.current.getBoundingClientRect();
        const viewW = rect.width;
        const viewH = rect.height;

        if (viewW === 0 || viewH === 0) return;

        // Calculate bounding box in percentage
        const minX = Math.min(...slotsWithCoords.map(s => s.coordinate_x));
        const maxX = Math.max(...slotsWithCoords.map(s => s.coordinate_x));
        const minY = Math.min(...slotsWithCoords.map(s => s.coordinate_y));
        const maxY = Math.max(...slotsWithCoords.map(s => s.coordinate_y));

        const padding = 12; // Slightly more padding
        const boxWidth = Math.max(5, (maxX - minX) + padding * 2);
        const boxHeight = Math.max(5, (maxY - minY) + padding * 2);

        const pixelWidth = (boxWidth / 100) * 1200;
        const pixelHeight = (boxHeight / 100) * 800;

        const zoomX = viewW / pixelWidth;
        const zoomY = viewH / pixelHeight;
        let newZoom = Math.min(zoomX, zoomY);

        newZoom = Math.max(0.4, Math.min(1.8, newZoom)); // Max zoom 1.8 for better aesthetics
        setZoom(newZoom);

        // Center and Scroll
        setTimeout(() => {
            if (mapAreaRef.current) {
                const centerX = ((minX + maxX) / 2 / 100) * 1200 * newZoom;
                const centerY = ((minY + maxY) / 2 / 100) * 800 * newZoom;
                mapAreaRef.current.scrollLeft = centerX - (viewW / 2);
                mapAreaRef.current.scrollTop = centerY - (viewH / 2);
            }
            // Small delay to ensure everything is rendered before hiding loading
            setTimeout(() => {
                setIsMapInitialLoading(false);
                setIsTransitioning(false);
            }, 50);
        }, 100); // 100ms for smoother transition after background change
    }, [slots]);

    // Auto-fit on zone change or when slots data changes
    const lastSessionKeyRef = useRef('');

    useEffect(() => {
        const sessionKey = `${selectedZone}-${slots.length}`;
        if (slots.length > 0 && lastSessionKeyRef.current !== sessionKey) {
            // If it's not the first load, show transitioning state
            if (lastSessionKeyRef.current !== '') {
                setIsTransitioning(true);
            }
            // Small delay to ensure the DOM is ready for getBoundingClientRect
            const timer = setTimeout(() => {
                fitToSlots(slots);
                lastSessionKeyRef.current = sessionKey;
            }, 100);
            return () => clearTimeout(timer);
        }
    }, [selectedZone, slots, fitToSlots]);

    // Background image mapping based on zone
    const getBackgroundImage = (zoneName) => {
        if (!zoneName && zones.length > 0) {
            zoneName = zones[0].zone_name;
        }

        const zoneObj = zones?.find(z => z && z.zone_name === zoneName);
        if (zoneObj && zoneObj.image_url) {
            return `url("${API_URL}${zoneObj.image_url}")`;
        }

        // Final fallback for consistent aesthetics during transition
        if (zoneName === 'ชั้น G') return 'url("/parking-map-G1.png")';
        if (zoneName === 'ชั้น 2') return 'url("/parking-map-2.png")';
        if (zoneName === 'รอบอาคาร') return 'url("/parking-map-outdoor.png")';
        if (zoneName === 'ที่จอดมอไซค์') return 'url("/parking-map-bike.png")';
        return 'url("/parking-map.png")'; // Default
    };

    // Filter slots that have coordinates if not in admin mode,
    // or show all slots in admin mode to allow positioning.
    const displaySlots = isAdmin ? localSlots : localSlots.filter(s => s.coordinate_x !== null);

    const getSlotColor = (slotId) => {
        const slot = slots.find(s => s.id === slotId);
        if (slot && (slot.is_activate === 0 || slot.is_activate === false)) {
            return 'bg-slate-200 border-slate-300 text-slate-400 opacity-50 cursor-not-allowed';
        }
        const log = logs[slotId];
        // Empty slots - use white with a clear blue-ish border for better visibility
        if (!log) return 'bg-white border-blue-200 text-slate-400 shadow-sm';

        const status = log.status;
        if (status === 'Normal') return 'bg-emerald-500 border-emerald-600 text-white shadow-sm';
        if (status === 'Unclear') return 'bg-amber-500 border-amber-600 text-white shadow-sm';
        if (status === 'Empty') return 'bg-slate-300 border-slate-400 text-slate-600 shadow-sm opacity-80';
        if (status === 'No-Sticker') return 'bg-rose-500 border-rose-600 text-white shadow-sm';
        if (status === 'Visitor') return 'bg-sky-500 border-sky-600 text-white shadow-sm';
        // Default / Unknown status - use indigo instead of grey
        return 'bg-indigo-500 border-indigo-600 text-white shadow-sm';
    };

    const handleMouseDown = (e, slotId = null) => {
        if (!isAdmin || !isEditMode) return;

        // If locked, we want slot clicks to bubble up to the container for panning/scrolling
        if (isLocked && slotId) return;

        if (e.target.closest('button')) return;

        const rect = containerRef.current.getBoundingClientRect();
        const x = ((e.clientX - rect.left) / rect.width) * 100;
        const y = ((e.clientY - rect.top) / rect.height) * 100;

        if (slotId) {
            e.stopPropagation();
            e.preventDefault();

            let newIds = new Set(selectedSlotIds);
            if (e.shiftKey) {
                if (newIds.has(slotId)) newIds.delete(slotId);
                else newIds.add(slotId);
            } else if (!newIds.has(slotId)) {
                newIds = new Set([slotId]);
            }

            setSelectedSlotIds(newIds);
            setIsDragging(true);
            setDragStartPos({
                x, y,
                triggerSlotId: slotId,
                initialSlots: localSlots.filter(s => newIds.has(s.id))
            });
        } else if ((e.shiftKey || isAddModeMap) && onMapClickToAdd) {
            // Shift + Click or Add Mode on background to add a new slot at this position
            onMapClickToAdd(x, y);
        } else {
            // Start Marquee Selection
            setMarquee({ start: { x, y }, end: { x, y } });
            if (!e.shiftKey) setSelectedSlotIds(new Set());
        }
    };

    const handleMouseMove = (e) => {
        if (!isAdmin || !isEditMode) return;
        const rect = containerRef.current.getBoundingClientRect();
        const x = ((e.clientX - rect.left) / rect.width) * 100;
        const y = ((e.clientY - rect.top) / rect.height) * 100;

        if (isDragging && dragStartPos) {
            if (isLocked) return; // Block slot movement if locked
            let dx = x - dragStartPos.x;
            let dy = y - dragStartPos.y;

            const SNAP_THRESHOLD = 1.0;
            let snapXLine = null;
            let snapYLine = null;

            // Snap the primary (trigger) slot to other non-selected slots
            const triggerSlot = dragStartPos.initialSlots.find(s => s.id === dragStartPos.triggerSlotId);
            if (triggerSlot) {
                let targetX = triggerSlot.coordinate_x + dx;
                let targetY = triggerSlot.coordinate_y + dy;

                localSlots.forEach(s => {
                    if (selectedSlotIds.has(s.id) || s.coordinate_x === null) return;

                    if (Math.abs(targetX - s.coordinate_x) < SNAP_THRESHOLD) {
                        targetX = s.coordinate_x;
                        snapXLine = targetX;
                    }
                    if (Math.abs(targetY - s.coordinate_y) < SNAP_THRESHOLD) {
                        targetY = s.coordinate_y;
                        snapYLine = targetY;
                    }
                });

                // Recalculate dx/dy based on snapped target
                dx = targetX - triggerSlot.coordinate_x;
                dy = targetY - triggerSlot.coordinate_y;
            }

            setGuides({ x: snapXLine, y: snapYLine });

            setLocalSlots(prev => prev.map(s => {
                const initial = dragStartPos.initialSlots.find(is => is.id === s.id);
                if (initial) {
                    return {
                        ...s,
                        coordinate_x: Math.max(0, Math.min(100, initial.coordinate_x + dx)),
                        coordinate_y: Math.max(0, Math.min(100, initial.coordinate_y + dy))
                    };
                }
                return s;
            }));
        } else if (marquee) {
            // Marquee is always allowed (it's just selection)
            setMarquee(prev => ({ ...prev, end: { x, y } }));

            // Selection logic
            const minX = Math.min(marquee.start.x, x);
            const maxX = Math.max(marquee.start.x, x);
            const minY = Math.min(marquee.start.y, y);
            const maxY = Math.max(marquee.start.y, y);

            const newlySelected = new Set(selectedSlotIds);
            localSlots.forEach(s => {
                if (s.coordinate_x >= minX && s.coordinate_x <= maxX &&
                    s.coordinate_y >= minY && s.coordinate_y <= maxY) {
                    newlySelected.add(s.id);
                }
            });
            setSelectedSlotIds(newlySelected);
        }
    };

    const handleMouseUp = () => {
        if (isDragging && selectedSlotIds.size > 0) {
            saveCoordinates(selectedSlotIds, localSlots);
        }
        setIsDragging(false);
        setMarquee(null);
        setDragStartPos(null);
        setGuides({ x: null, y: null });
    };

    const saveCoordinates = async (slotIds, currentSlots = localSlots) => {
        const idsArray = Array.from(slotIds);
        if (idsArray.length === 0) return;

        try {
            await Promise.all(idsArray.map(async id => {
                const slot = currentSlots.find(s => s.id === id);
                if (!slot) return null;
                return axios.put(`${API_URL}/api/slots/${id}/coordinates`, {
                    coordinate_x: slot.coordinate_x,
                    coordinate_y: slot.coordinate_y,
                    rotation: slot.rotation || 0
                }, {
                    headers: { Authorization: `Bearer ${token}` }
                });
            }));
            if (onRefresh) onRefresh();
        } catch (err) {
            if (err.response?.status === 401) return;
            console.error('Error saving coordinates:', err);
        }
    };

    const handleRotate = (slotIds) => {
        if (!isAdmin || !isEditMode || isLocked) return;
        const updatedSlots = localSlots.map(s =>
            slotIds.has(s.id) ? { ...s, rotation: ((s.rotation || 0) + 45) % 360 } : s
        );
        setLocalSlots(updatedSlots);

        // Auto-save on rotate with the latest data
        saveCoordinates(slotIds, updatedSlots);
    };

    // Keyboard shortcuts
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (selectedSlotIds.size === 0 || !isEditMode) return;
            if (isLocked && (e.key.toLowerCase() === 'r' || e.key.toLowerCase() === 's')) return;
            if (e.key.toLowerCase() === 'r') {
                handleRotate(selectedSlotIds);
            }
            if (e.key.toLowerCase() === 's') {
                saveCoordinates(selectedSlotIds, localSlots);
            }
            if (e.key === 'Escape') {
                setSelectedSlotIds(new Set());
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedSlotIds, isEditMode, localSlots]);

    useEffect(() => {
        const mapArea = mapAreaRef.current;
        if (!mapArea) return;

        const onTouchStart = (e) => {
            if (e.touches.length === 2) {
                const dist = Math.hypot(
                    e.touches[0].clientX - e.touches[1].clientX,
                    e.touches[0].clientY - e.touches[1].clientY
                );
                touchStartDistRef.current = dist;
                touchStartZoomRef.current = zoomRef.current;

                if (e.cancelable) e.preventDefault();
            }
        };

        const onTouchMove = (e) => {
            if (e.touches.length === 2 && touchStartDistRef.current !== null && touchStartZoomRef.current !== null) {
                const dist = Math.hypot(
                    e.touches[0].clientX - e.touches[1].clientX,
                    e.touches[0].clientY - e.touches[1].clientY
                );

                const sensitivity = 0.8;
                const scale = (dist / touchStartDistRef.current);
                const adjustedScale = 1 + (scale - 1) * sensitivity;

                const newZoom = Math.max(0.4, Math.min(2.5, touchStartZoomRef.current * adjustedScale));
                setZoom(newZoom);

                if (e.cancelable) e.preventDefault();
            }
        };

        const onTouchEnd = (e) => {
            if (e.touches.length < 2) {
                touchStartDistRef.current = null;
                touchStartZoomRef.current = null;
            }
        };

        mapArea.addEventListener('touchstart', onTouchStart, { passive: false });
        mapArea.addEventListener('touchmove', onTouchMove, { passive: false });
        mapArea.addEventListener('touchend', onTouchEnd, { passive: false });

        return () => {
            mapArea.removeEventListener('touchstart', onTouchStart);
            mapArea.removeEventListener('touchmove', onTouchMove);
            mapArea.removeEventListener('touchend', onTouchEnd);
        };
    }, []);

    const toggleFullscreen = () => {
        if (isFullscreen) {
            const exitFS = document.exitFullscreen ||
                document.webkitExitFullscreen ||
                document.mozCancelFullScreen ||
                document.msExitFullscreen;

            if (exitFS && (document.fullscreenElement || document.webkitFullscreenElement || document.mozFullScreenElement || document.msFullscreenElement)) {
                try {
                    const promise = exitFS.call(document);
                    if (promise && typeof promise.catch === 'function') {
                        promise.catch(err => console.error(err));
                    }
                } catch (e) {
                    console.error('Error exiting full-screen', e);
                }
            }
            setIsFullscreen(false);
        } else {
            const root = mapRootRef.current || document.documentElement;
            const requestFS = root.requestFullscreen ||
                root.webkitRequestFullscreen ||
                root.mozRequestFullScreen ||
                root.msRequestFullscreen;

            // Set local state anyway so CSS fallback (h-screen, inset-0) can take effect
            setIsFullscreen(true);

            // Only use Native Fullscreen on Desktop
            // On Mobile, native fullscreen hides elements outside the root (like modals)
            const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
            if (requestFS && !isMobile) {
                try {
                    const promise = requestFS.call(root);
                    if (promise && typeof promise.catch === 'function') {
                        promise.catch(err => {
                            console.warn('Native fullscreen rejected.', err);
                        });
                    }
                } catch (e) {
                    console.warn('Native fullscreen not fully supported.', e);
                }
            }
        }
    };

    useEffect(() => {
        const handleFullscreenChange = () => {
            setIsFullscreen(!!(
                document.fullscreenElement ||
                document.webkitFullscreenElement ||
                document.mozFullScreenElement ||
                document.msFullscreenElement
            ));
        };

        document.addEventListener('fullscreenchange', handleFullscreenChange);
        document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
        document.addEventListener('mozfullscreenchange', handleFullscreenChange);
        document.addEventListener('MSFullscreenChange', handleFullscreenChange);

        return () => {
            document.removeEventListener('fullscreenchange', handleFullscreenChange);
            document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
            document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
            document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
        };
    }, []);

    const handleWheel = (e) => {
        if (e.ctrlKey || e.metaKey) {
            e.preventDefault();
            const delta = e.deltaY;
            const zoomStep = 0.05;
            if (delta > 0) {
                setZoom(prev => Math.max(0.5, prev - zoomStep));
            } else {
                setZoom(prev => Math.min(2.5, prev + zoomStep));
            }
        }
    };

    const bgImage = getBackgroundImage(selectedZone);

    return (
        <div
            ref={mapRootRef}
            className={`flex flex-col ${isFullscreen ? 'fixed inset-0 z-[1050] w-full h-screen h-[100dvh] bg-slate-100' : 'relative w-full h-full bg-slate-100 rounded-none md:rounded-3xl border-none md:border border-slate-200/50'} overflow-hidden`}
        >
            {/* Floating Controls Overlay */}
            <div className="absolute top-4 left-4 right-4 z-40 flex items-start justify-between pointer-events-none">
                {/* Left: Title & Admin */}
                <div className="flex flex-col gap-2 items-start">
                    {isFullscreen && (
                        <div className="relative pointer-events-auto">
                            <button
                                onClick={() => setIsZoneListOpen(!isZoneListOpen)}
                                className={`flex items-center bg-white/90 backdrop-blur-xl rounded-2xl p-1.5 shadow-lg border active:scale-95 ${isZoneListOpen ? '' : ''}`}
                            >
                                <div className="w-8 h-8 bg-gradient-to-tr from-blue-600 to-indigo-600 rounded-xl flex items-center justify-center flex-shrink-0 shadow-sm">
                                    <RotateCw className={`w-4 h-4 text-white transition-transform duration-500 ${isZoneListOpen ? 'rotate-180' : ''}`} />
                                </div>
                                <span className="font-black text-[14px] text-slate-800 tracking-tight px-3">
                                    {selectedZone}
                                </span>
                                <div className={`mr-1 transition-transform duration-300 ${isZoneListOpen ? 'rotate-180 text-blue-600' : 'text-slate-400'}`}>
                                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M19 9l-7 7-7-7" /></svg>
                                </div>
                            </button>

                            {isZoneListOpen && (
                                <div className="absolute top-full left-0 mt-2 w-full bg-white/95 backdrop-blur-2xl rounded-2xl shadow-2xl border border-slate-100 overflow-hidden animate-in fade-in zoom-in-95 duration-200 origin-top-left py-1">
                                    <div className="max-h-[300px] overflow-y-auto py-1 scrollbar-hide">
                                        {[...new Set((zones || []).map(z => z?.zone_name).filter(Boolean))].map(zoneName => (
                                            <button
                                                key={zoneName}
                                                onClick={() => {
                                                    onZoneChange?.(zoneName);
                                                    setIsZoneListOpen(false);
                                                }}
                                                className={`w-full text-left px-4 py-3 text-[13px] font-black tracking-tight flex items-center justify-between transition-colors ${selectedZone === zoneName ? 'bg-blue-50 text-blue-600' : 'text-slate-600 hover:bg-slate-50'}`}
                                            >
                                                <span>{zoneName}</span>
                                                {selectedZone === zoneName && (
                                                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" /></svg>
                                                )}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {isAdmin && (
                        <div className="pointer-events-auto flex items-center bg-white/80 backdrop-blur-xl rounded-2xl p-1 shadow-lg border border-slate-200/50 gap-1">
                            {isEditMode && (
                                <button
                                    onClick={() => setIsLocked(!isLocked)}
                                    className={`p-2 rounded-xl transition-all ${!isLocked ? 'bg-rose-50 text-rose-600 hover:bg-rose-100' : 'bg-emerald-50 text-emerald-600 hover:bg-emerald-100'}`}
                                    title={isLocked ? "Unlock (Pan Mode)" : "Lock to Move/Rotate"}
                                >
                                    {isLocked ? <Lock className="w-4 h-4" /> : <Unlock className="w-4 h-4" />}
                                </button>
                            )}
                            <button
                                onClick={() => {
                                    setIsEditMode(!isEditMode);
                                    if (isEditMode) {
                                        setSelectedSlotIds(new Set());
                                        setIsAddModeMap(false);
                                    }
                                }}
                                className={`flex items-center gap-1 px-2 py-1 rounded-md text-[9px] font-black transition-all ${isEditMode
                                    ? 'bg-gradient-to-r from-amber-500 to-amber-600 text-white shadow-amber-500/20'
                                    : 'bg-white text-slate-600 hover:bg-slate-50'
                                    }`}
                            >
                                <Move className="w-2.5 h-2.5" />
                                <span>{isEditMode ? 'Exit Edit' : 'Edit'}</span>
                            </button>
                            {isEditMode && (
                                <button
                                    onClick={() => setIsAddModeMap(!isAddModeMap)}
                                    className={`flex items-center gap-1 px-2 py-1 rounded-md text-[9px] font-black transition-all ${isAddModeMap
                                        ? 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white shadow-blue-500/20'
                                        : 'bg-white text-slate-600 hover:bg-slate-50'
                                        }`}
                                    title="Click map to add slot"
                                >
                                    <Plus className="w-2.5 h-2.5" />
                                    <span>Add Slot</span>
                                </button>
                            )}
                        </div>
                    )}
                </div>

                {/* Right: Main Controls Vertical Stack */}
                <div className="flex flex-col items-end gap-3">
                    <div className="pointer-events-auto flex flex-col items-center bg-white/80 backdrop-blur-xl rounded-[1.25rem] p-1.5 shadow-lg shadow-black/5 border border-slate-200/50 gap-1">
                        <button
                            onClick={() => setZoom(prev => Math.min(2.5, prev + 0.1))}
                            className="p-2.5 rounded-xl hover:bg-blue-50 transition-all text-slate-500 hover:text-blue-600 active:scale-95 bg-white shadow-sm"
                            title="Zoom In"
                        >
                            <Plus className="w-4 h-4 stroke-[2.5]" />
                        </button>
                        <div className="text-[9px] font-black tracking-tighter text-slate-400 py-1">{Math.round(zoom * 100)}%</div>
                        <button
                            onClick={() => setZoom(prev => Math.max(0.5, prev - 0.1))}
                            className="p-2.5 rounded-xl hover:bg-blue-50 transition-all text-slate-500 hover:text-blue-600 active:scale-95 bg-white shadow-sm"
                            title="Zoom Out"
                        >
                            <Minus className="w-4 h-4 stroke-[2.5]" />
                        </button>
                    </div>

                    <div className="pointer-events-auto flex flex-col items-center bg-white/80 backdrop-blur-xl rounded-[1.25rem] p-1.5 shadow-lg shadow-black/5 border border-slate-200/50 gap-1">
                        <button
                            onClick={() => fitToSlots()}
                            className="p-2.5 rounded-xl transition-all text-blue-600 bg-blue-50 hover:bg-blue-100 active:scale-95 font-bold shadow-sm"
                            title="Auto Fit"
                        >
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" /></svg>
                        </button>
                        <button
                            onClick={() => setIsDimmed(!isDimmed)}
                            className={`p-2.5 rounded-xl transition-all shadow-sm ${isDimmed ? 'bg-indigo-50 text-indigo-600 font-bold' : 'bg-white text-slate-500 hover:bg-slate-50'}`}
                            title="Toggle Dim View"
                        >
                            <svg className="w-4 h-4" fill={isDimmed ? "currentColor" : "none"} viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                            </svg>
                        </button>
                        <button
                            onClick={toggleFullscreen}
                            className={`p-2.5 rounded-xl transition-all shadow-sm ${isFullscreen ? 'bg-slate-800 text-white' : 'bg-white text-slate-500 hover:bg-slate-50'}`}
                            title={isFullscreen ? "Exit Fullscreen" : "Enter Fullscreen"}
                        >
                            {isFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
                        </button>
                    </div>
                </div>
            </div>

            {/* Bottom Center Audit Filter Overlay - Root Level */}
            {setAuditFilter && (
                <div className={`${isFullscreen ? 'fixed bottom-16 z-[9999]' : 'absolute bottom-6 z-40'} inset-x-0 px-4 pointer-events-none flex justify-center pb-safe animate-in slide-in-from-bottom-8 duration-700 ease-out`}>
                    <div className="pointer-events-auto flex items-center bg-white/95 backdrop-blur-2xl rounded-full p-1 shadow-[0_15px_40px_rgba(0,0,0,0.25)] border border-slate-200/50 gap-0.5 ring-1 ring-black/5">
                        {[
                            { id: 'All', label: 'ทั้งหมด', icon: <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M4 6h16M4 12h16m-7 6h7" /></svg> },
                            { id: 'Audited', label: 'ตรวจแล้ว', icon: <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" /></svg> },
                            { id: 'Pending', label: 'ยังไม่ตรวจ', icon: <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg> }
                        ].map(f => {
                            const count = f.id === 'All'
                                ? slots.length
                                : f.id === 'Audited'
                                    ? slots.filter(s => logs[s.id]).length
                                    : slots.filter(s => !logs[s.id]).length;

                            return (
                                <button
                                    key={f.id}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setAuditFilter(f.id);
                                    }}
                                    className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-[9px] font-black transition-all active:scale-95 ${auditFilter === f.id
                                        ? (f.id === 'Audited' ? 'bg-emerald-600 text-white shadow-emerald-500/20' : f.id === 'Pending' ? 'bg-amber-600 text-white shadow-amber-500/20' : 'bg-blue-600 text-white shadow-blue-500/20') + ' shadow-md border border-white/10'
                                        : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
                                        }`}
                                >
                                    {f.icon}
                                    <span className="tracking-tight">{f.label}</span>
                                    <span className={`text-[8px] ${auditFilter === f.id ? 'text-white/70' : 'text-slate-400 font-bold'}`}>({count})</span>
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Safe Area Gradient (Only in Fullscreen) */}
            {isFullscreen && (
                <div className="fixed bottom-0 inset-x-0 h-32 bg-gradient-to-t from-black/20 to-transparent pointer-events-none z-[4999]" />
            )}

            {/* Map Area */}
            <div className="flex-1 relative overflow-hidden bg-transparent w-full h-full z-10 cursor-move">
                {/* Loading / Transition Overlay */}
                {(isMapInitialLoading || isTransitioning) && (
                    <div className="absolute inset-0 z-[100] flex items-center justify-center bg-slate-100/90 backdrop-blur-sm transition-opacity duration-300">
                        <div className="flex flex-col items-center gap-4 bg-white/90 p-6 rounded-2xl shadow-xl">
                            <div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                            <div className="flex flex-col items-center">
                                <span className="text-[13px] font-black text-slate-800 tracking-tight">
                                    {isMapInitialLoading ? 'Preparing Map Layout...' : 'Switching Zone...'}
                                </span>
                            </div>
                        </div>
                    </div>
                )}

                <div
                    ref={mapAreaRef}
                    className="absolute inset-0 overflow-auto p-4 custom-scrollbar select-none"
                    style={{ touchAction: 'pan-x pan-y' }}
                    onMouseDown={(e) => handleMouseDown(e)}
                    onMouseMove={handleMouseMove}
                    onMouseUp={handleMouseUp}
                    onMouseLeave={handleMouseUp}
                    onWheel={handleWheel}
                >
                    <div
                        style={{
                            width: `${1200 * zoom}px`,
                            height: `${800 * zoom}px`,
                            overflow: 'hidden',
                        }}
                        className="mx-auto"
                    >
                        <div
                            ref={containerRef}
                            className="relative bg-white shadow-2xl transition-transform origin-top-left"
                            style={{
                                width: '1200px',
                                height: '800px',
                                transform: `scale(${zoom})`,
                                backgroundImage: isDimmed ? `linear-gradient(rgba(0,0,0,0.3), rgba(0,0,0,0.3)), ${bgImage}` : bgImage,
                                backgroundSize: 'cover',
                                backgroundPosition: 'center',
                                cursor: isDragging ? 'grabbing' : 'auto',
                                transition: 'background-image 0.3s ease'
                            }}
                        >
                            {/* Placeholder for missing image */}
                            <div className="absolute inset-0 flex items-center justify-center pointer-events-none opacity-10">
                                <span className="text-4xl font-black uppercase tracking-widest text-slate-300">Floor Plan Background</span>
                            </div>

                            {/* Marquee Selection Box */}
                            {marquee && (
                                <div
                                    className="absolute border-2 border-blue-500 bg-blue-500/10 z-[100] pointer-events-none"
                                    style={{
                                        left: `${Math.min(marquee.start.x, marquee.end.x)}%`,
                                        top: `${Math.min(marquee.start.y, marquee.end.y)}%`,
                                        width: `${Math.abs(marquee.end.x - marquee.start.x)}%`,
                                        height: `${Math.abs(marquee.end.y - marquee.start.y)}%`,
                                    }}
                                />
                            )}

                            {/* Alignment Guides */}
                            {guides.x !== null && (
                                <div
                                    className="absolute top-0 bottom-0 border-l-2 border-blue-400 border-dashed z-40 pointer-events-none"
                                    style={{ left: `${guides.x}%` }}
                                />
                            )}
                            {guides.y !== null && (
                                <div
                                    className="absolute left-0 right-0 border-t-2 border-blue-400 border-dashed z-40 pointer-events-none"
                                    style={{ top: `${guides.y}%` }}
                                />
                            )}

                            {displaySlots.map(slot => {
                                const isSelected = selectedSlotIds.has(slot.id);
                                const hasPos = slot.coordinate_x !== null && slot.coordinate_y !== null;

                                const style = {
                                    left: hasPos ? `${slot.coordinate_x}%` : '50%',
                                    top: hasPos ? `${slot.coordinate_y}%` : '50%',
                                    transform: `translate(-50%, -50%) rotate(${slot.rotation || 0}deg)`,
                                };

                                const isFilteredOut = auditFilter !== 'All' && (
                                    (auditFilter === 'Audited' && !logs[slot.id]) ||
                                    (auditFilter === 'Pending' && logs[slot.id])
                                );

                                return (
                                    <div
                                        key={slot.id}
                                        onMouseDown={(e) => handleMouseDown(e, slot.id)}
                                        onClick={(e) => {
                                            if (!isEditMode) {
                                                if (slot.is_activate !== 0 && slot.is_activate !== false) {
                                                    onSlotClick(slot.id);
                                                }
                                            }
                                        }}
                                        className={`absolute w-10 h-6 border-[1.5px] ${(slot.is_double_parking === 1 || slot.is_double_parking === true) ? 'border-dashed border-2' : ''} flex items-center justify-center transition-all duration-300 cursor-pointer group ${isSelected && isEditMode ? 'ring-2 ring-blue-500 ring-offset-2 z-50 scale-110 shadow-xl' : 'z-10 shadow-sm'
                                            } ${isFilteredOut ? 'bg-slate-100 border-slate-200 text-slate-400 opacity-20 blur-[0.5px] pointer-events-none scale-[0.85]' : getSlotColor(slot.id)} rounded-sm`}
                                        style={style}
                                    >
                                        <span className="font-black text-[8px] tracking-tighter">
                                            {slot.slot_name}
                                        </span>

                                        {/* Admin controls on hover (only for single selection) */}
                                        {isAdmin && isEditMode && isSelected && selectedSlotIds.size === 1 && !isDragging && !isLocked && (
                                            <div className="absolute -top-12 left-1/2 -translate-x-1/2 flex gap-1 bg-white p-1 rounded-lg shadow-xl border border-slate-100 z-[60] animate-in slide-in-from-bottom-2">
                                                <button
                                                    onClick={(e) => { e.stopPropagation(); handleRotate(new Set([slot.id])); }}
                                                    className="p-1.5 hover:bg-slate-100 rounded text-slate-600"
                                                    title="Rotate (R)"
                                                >
                                                    <RotateCw className="w-3.5 h-3.5" />
                                                </button>
                                                <button
                                                    onClick={(e) => { e.stopPropagation(); setSelectedSlotIds(new Set()); }}
                                                    className="p-1.5 hover:bg-red-100 rounded text-red-600"
                                                    title="Close"
                                                >
                                                    <X className="w-3.5 h-3.5" />
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>

                {/* Shortcut Hint */}
                {isAdmin && isEditMode && (
                    <div className="hidden md:flex bg-blue-600 px-4 py-2 text-[10px] font-bold text-white justify-between animate-in slide-in-from-bottom-2">
                        <div className="flex gap-4">
                            <span className="flex items-center gap-1.5">
                                <span className={`w-1.5 h-1.5 rounded-full ${isLocked ? 'bg-rose-400' : 'bg-white animate-pulse'}`}></span>
                                {isLocked ? 'MAP LOCKED (PAN MODE)' : 'AUTO-SAVE ENABLED'}
                            </span>
                            {isLocked ? (
                                <span className="text-rose-200">UNLOCK TO DRAG OR ROTATE</span>
                            ) : (
                                <span>DRAG TO POSITION • PRESS 'R' TO ROTATE</span>
                            )}
                            <span className="opacity-60 whitespace-nowrap">ZOOM: Ctrl + Scroll / Slider • MARQUEE: Drag background • ADD: Shift + Click / "+" Button</span>
                        </div>
                        <span className="opacity-80">Relative Coordinates (%)</span>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ParkingMap;
